import React, { useMemo, useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import usePageMeta from '../../utilities/usePageMeta';
import ArticlePage from './ArticlePage';
import calculatorBanner from '../../resources/css/images/calculator-banner.jpg';
import gif25 from '../../resources/css/images/25.gif';
import gif50 from '../../resources/css/images/50.gif';
import gif75 from '../../resources/css/images/75.gif';
import gif100 from '../../resources/css/images/100.gif';
import gif150 from '../../resources/css/images/150.gif';
import gif200 from '../../resources/css/images/200.gif';
import gif250 from '../../resources/css/images/250.gif';
import gif300 from '../../resources/css/images/300.gif';

/** Cubic feet each item is assumed to take up. Unchanged from the original calculator. */
const ITEMS = [
    { id: 1, name: 'Chair overstuffed', volume: 25 },
    { id: 2, name: 'Sofa love seat', volume: 35 },
    { id: 3, name: 'Sofa 3 cushion', volume: 65 },
    { id: 4, name: 'Sofa 4 cushion', volume: 65 },
    { id: 5, name: 'Sofa sectional pieces', volume: 40 },
    { id: 6, name: 'Dresser', volume: 30 },
    { id: 7, name: 'Mattress', volume: 65 },
    { id: 8, name: 'Dining chairs', volume: 5 },
    { id: 9, name: 'Dining table', volume: 30 },
    { id: 10, name: 'Dishwasher', volume: 20 },
    { id: 11, name: 'Dryer', volume: 25 },
    { id: 12, name: 'Refrigerator under 15', volume: 45 },
    { id: 13, name: 'Refrigerator over 16', volume: 65 },
    { id: 14, name: 'Stove', volume: 18 },
    { id: 15, name: 'Washing machine', volume: 25 },
    { id: 16, name: 'Bicycle', volume: 6 },
    { id: 17, name: 'Boxes', volume: 2.5 },
    { id: 18, name: 'Patio chairs', volume: 6 },
    { id: 19, name: 'Patio table', volume: 15 },
];

/**
 * The fields, in the sections they appear under.
 *
 * Item 4 deliberately has no field: the original calculator never offered one, and adding it now
 * would change the numbers people have been given.
 */
const INPUT_GROUPS = [
    { name: 'Living Room', fields: [{ label: 'Chair (overstuffed)', itemId: 1 }, { label: 'Sofa (Love seat)', itemId: 2 }, { label: 'Sofa (3 cushion)', itemId: 3 }, { label: 'Sofa Sectional pc.', itemId: 5 }] },
    { name: 'Bedroom', fields: [{ label: 'Dresser', itemId: 6 }, { label: 'Mattress', itemId: 7 }] },
    { name: 'Dining Room', fields: [{ label: 'Dining Chair', itemId: 8 }, { label: 'Dining Table', itemId: 9 }] },
    { name: 'Kitchen & Appliances', fields: [{ label: 'Dishwasher', itemId: 10 }, { label: 'Dryer', itemId: 11 }, { label: 'Refrigerator (< 15 cu. ft.)', itemId: 12 }, { label: 'Refrigerator (> 16 cu. ft.)', itemId: 13 }, { label: 'Stove', itemId: 14 }, { label: 'Washing Machine', itemId: 15 }] },
    { name: 'Miscellaneous Items', fields: [{ label: 'Bicycle', itemId: 16 }, { label: 'Boxes', itemId: 17 }, { label: 'Patio Chair', itemId: 18 }, { label: 'Patio Table', itemId: 19 }] },
];

/** What each recommended size means, shown one row at a time. */
const UNIT_GUIDE = [
    {
        size: 25,
        dimensions: <>5&apos; x 5&apos;<br/> 25 sq. ft.<br/> (1.52m x 1.52m)<br/> (2.32 sq. m.)</>,
        visualize: <>4&apos;x8&apos; Trailer<br/> (1.22m x 2.44m Trailer)<br/> Small closet<br/> Half bathroom</>,
        holds: 'Chest of drawers or desk mattress set, several boxes and small items OR about 50 file boxes',
        image: gif25,
    },
    {
        size: 50,
        dimensions: <>5&apos; x 10&apos;<br/> 50 sq. ft.<br/> (1.52m x 3.05m)<br/> (4.65 sq. m.)</>,
        visualize: <>6&apos;x12&apos; Trailer<br/> (1.83m x 3.66m Trailer)<br/> Pickup Truck or Cargo Van<br/> Walk-in closet</>,
        holds: 'Mattress set, sofa, dining room set or chest of drawers, boxes and small items',
        image: gif50,
    },
    {
        size: 75,
        dimensions: <>5&apos; x 15&apos;<br/> 75 sq. ft.<br/> (1.52m x 4.57m)<br/> (6.97 sq. m.)</>,
        visualize: <>Large walk-in closet<br/> Small bedroom</>,
        holds: 'Standard furnishings of one bedroom apartment without appliances, or about 150 file boxes',
        image: gif75,
    },
    {
        size: 100,
        dimensions: <>10&apos; x 10&apos;<br/> 100 sq. ft.<br/> (3.05m x 3.05m)<br/> (9.29 sq. m.)</>,
        visualize: <>15&apos; Moving Truck<br/> (4.57m Moving Truck)<br/> Average size bedroom</>,
        holds: 'Furnishings of one bedroom apartment with refrigerator and washer/dryer, patio furniture and many boxes OR two office suites OR about 200 file boxes',
        image: gif100,
    },
    {
        size: 150,
        dimensions: <>10&apos; x 15&apos;<br/> 150 sq. ft.<br/> (3.05m x 4.57m)<br/> (13.94 sq. m.)</>,
        visualize: <>20&apos; Moving Truck<br/> (6.10m Moving Truck)<br/> Large bedroom</>,
        holds: 'Furnishings of two bedroom apartment or small house with appliances, patio furniture and many boxes OR a vehicle or small boat OR it is big enough to fit carpet rolls and building materials',
        image: gif150,
    },
    {
        size: 200,
        dimensions: <>10&apos; x 20&apos;<br/> 200 sq. ft.<br/> (3.05m x 6.10m)<br/> (18.58 sq. m.)</>,
        visualize: <>24&apos; Moving Truck<br/> (7.32m Moving Truck)<br/> Small one car garage</>,
        holds: 'Furnishings of two bedroom house with appliances, patio furniture and many boxes OR a vehicle or small boat OR construction equipment',
        image: gif200,
    },
    {
        size: 250,
        dimensions: <>10&apos; x 25&apos;<br/> 250 sq. ft.<br/> (3.05m x 7.62m)<br/> (23.23 sq. m.)</>,
        visualize: <>26&apos; Moving Truck<br/> (7.92m Moving Truck)<br/> Large one car garage</>,
        holds: 'Furnishings of three bedroom house with appliances, patio furniture and many boxes, garage extras and miscellaneous OR a vehicle or boat',
        image: gif250,
    },
    {
        size: 300,
        dimensions: <>10&apos; x 30&apos;<br/> 300 sq. ft.<br/> (3.05m x 9.14m)<br/> (27.87 sq. m.)</>,
        visualize: <>Extra long one car garage</>,
        holds: 'Furnishings of four or five bedroom house with appliances, patio furniture and many boxes, garage extras and miscellaneous OR a vehicle or boat',
        image: gif300,
    },
];

/** The sizes the facility actually rents, taken from the guide so the two cannot drift apart. */
const STOCKED_SIZES = UNIT_GUIDE.map((unit) => unit.size);

/**
 * Converts a total volume in cubic feet to a recommended unit size.
 *
 * The arithmetic and the size bumps are carried over from the original calculator unchanged. What is
 * new is the last step: the original could land on 175, 225 or 275, which are not sizes anyone can
 * rent and which the guide has no row for, so the page went blank for anyone whose belongings
 * happened to total between roughly 786 and 915 cubic feet, or 1047 and 1176, or over 1307.
 */
function recommendedSize(cubicFeet) {
    const rounded = Math.floor((cubicFeet + 24) / 25) * 25;

    const bumped = {
        125: 150,
        150: 200,
        200: 250,
        250: 300,
    }[rounded] ?? rounded;

    // Outside the range we rent; the page says so rather than naming a size.
    if (bumped <= 0 || bumped > STOCKED_SIZES[STOCKED_SIZES.length - 1]) {
        return bumped;
    }

    return STOCKED_SIZES.find((size) => size >= bumped) ?? bumped;
}

export default function SpaceCalculator() {
    const [amounts, setAmounts] = useState({});

    usePageMeta({
        title: 'Storage Space Calculator',
        description: 'Estimate the storage unit size you need. Add your furniture, appliances and boxes to get a recommended square footage for our East Troy, WI storage units.',
        path: '/calculator',
    });

    const recommended = useMemo(() => {
        const cubicFeet = ITEMS.reduce(
            (total, item) => total + (item.volume * (amounts[item.id] ?? 0)), 0);

        return recommendedSize(Math.round(cubicFeet * 1.15 / 6));
    }, [amounts]);

    const match = UNIT_GUIDE.find((unit) => unit.size === recommended);

    function setAmount(itemId, value) {
        const parsed = Number.parseInt(value, 10);

        setAmounts((current) => ({
            ...current,
            // A blank field means none rather than zero, so the box can be cleared.
            [itemId]: Number.isNaN(parsed) || parsed < 0 ? 0 : parsed,
        }));
    }

    return (
        <ArticlePage
            title={'Storage Space Calculator'}
            image={calculatorBanner}
            imageAlt={'A row of storage units at the East Troy facility'}
            imageWidth={1600}
            imageHeight={484}
        >
            <p>Not sure what size unit you need? Enter what you plan to store and we will suggest a size. The estimate allows for walkways and stacking, so treat it as a starting point rather than an exact figure.</p>

            <Box component={'form'} sx={{ marginBottom: '2.5em' }}>
                {INPUT_GROUPS.map((group) => (
                    <Box key={group.name} sx={{ marginBottom: '1.75em' }}>
                        <Typography
                            variant={'h6'}
                            component={'h2'}
                            sx={{ fontWeight: 700, marginBottom: '12px' }}
                        >
                            {group.name}
                        </Typography>

                        <Grid container spacing={2}>
                            {group.fields.map((field) => (
                                <Grid item xs={6} sm={4} md={2} key={field.itemId}>
                                    <TextField
                                        fullWidth
                                        size={'small'}
                                        type={'number'}
                                        label={field.label}
                                        placeholder={'0'}
                                        value={amounts[field.itemId] ?? ''}
                                        onChange={(event) => setAmount(field.itemId, event.target.value)}
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                ))}
            </Box>

            <Typography variant={'h5'} component={'h2'} sx={{ fontWeight: 700, marginBottom: '8px' }}>
                Your recommended size
            </Typography>

            {recommended > 0 &&
                <Typography
                    sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        fontSize: '1.6rem',
                        letterSpacing: '-0.025em',
                        marginBottom: '16px',
                    }}
                >
                    {recommended} sq. ft.
                </Typography>
            }

            {match &&
                <>
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{ border: '1px solid rgba(0, 0, 0, 0.08)', borderRadius: 2, marginBottom: '2em' }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f7f8f9' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Unit size</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>How to visualize</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>What it will hold</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Unit visualization</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                                        {match.dimensions}
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>{match.visualize}</TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>{match.holds}</TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        <Box
                                            component={'img'}
                                            src={String(match.image)}
                                            alt={`Floor plan of a ${match.size} square foot storage unit`}
                                            sx={{ maxWidth: '100%', height: 'auto' }}
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            }

            {recommended > 300 &&
                <>
                    <h3>Please give us a call</h3>

                    <p>Your specific estimation may require assistance from one of our professionals. Please give us a call at (414) 659-3045, or head over to our <a href="/contact">Contact Us</a> page for other ways to reach us.</p>
                </>
            }

            {recommended <= 0 &&
                <>
                    <h3>Please add items</h3>

                    <p>It appears that there aren&apos;t enough items in the fields above.  Please enter how many you have and this space will update accordingly.</p>
                </>
            }
        </ArticlePage>
    );
}
